import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "@emotion/react/jsx-runtime";
import { memo, useEffect, useRef, useState, } from "react";
import FormatCrypto from "@hl/base-components/lib/FormatCrypto";
import { CheckImported, CheckVerified, } from "@hl/base-components/lib/assets/icons.generated";
import { WEIGHT_BOLD } from "@hl/base-components/lib/theme/typography";
import { Anchor, Box, Flex, Group, Popover, Stack, Text, UnstyledButton, useMantineTheme, } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useCollections } from "@reservoir0x/reservoir-kit-ui";
import { IconArrowRightBar } from "@tabler/icons";
import ReactMarkdown from "react-markdown";
import gfm from "remark-gfm";
import ProfilePlaceholderImage from "../../assets/images/profile-placeholder.png";
import { getMintPageUrl, USER_ROUTES } from "../../config";
import { networkLookup } from "../../utils/blockExplorer";
import { getNativeCurrencyName } from "../../utils/currency";
import { getProfileDisplayName } from "../../utils/profile";
import { User } from "../auth/User";
import { ResizedImage } from "../image";
import Socials from "../socials/Socials";
import { useCollectionPopupPreviewQuery, useUserPopupPreviewQuery, } from "./queries.graphql.generated";
const DescriptionMarkdown = ({ description, hasExpand, numChars, disableVideos, lineClamp, noPopover, ...rest }) => {
    var _a;
    const enableExpanded = description.length > (numChars !== null && numChars !== void 0 ? numChars : 0);
    const [expanded, setExpanded] = useState(!hasExpand);
    const descriptionClean = (_a = description === null || description === void 0 ? void 0 : description.replace(/\\n/g, "\n")) !== null && _a !== void 0 ? _a : "";
    const finalText = expanded || !enableExpanded
        ? descriptionClean
        : `${descriptionClean.slice(0, numChars)}...`;
    return (_jsxs(Stack, { spacing: 0, children: [_jsx(Text, { size: "sm", ...rest, onClick: () => hasExpand && enableExpanded ? setExpanded((exp) => !exp) : undefined, lineClamp: lineClamp, children: _jsx(ReactMarkdown, { remarkPlugins: [gfm], components: {
                        a: (props) => {
                            var _a;
                            const href = ((_a = props.node.properties) === null || _a === void 0 ? void 0 : _a.href) || "";
                            if ((href.includes("collection:") || href.includes("user:")) &&
                                !noPopover) {
                                return _jsx(LinkWithPopupPreview, { ...props, size: rest.size });
                            }
                            return (_jsx(Anchor, { ...props, sx: {
                                    fontSize: rest.size || 14,
                                    textDecoration: "underline",
                                    wordBreak: "break-word",
                                    textUnderlineOffset: 3,
                                } }));
                        },
                        img: (props) => {
                            var _a, _b;
                            if (disableVideos) {
                                return null;
                            }
                            if ((_a = props.src) === null || _a === void 0 ? void 0 : _a.startsWith("https://www.youtube.com/embed/")) {
                                return (_jsx("iframe", { width: "100%", style: { aspectRatio: "16/9" }, src: props.src, title: (_b = props.alt) !== null && _b !== void 0 ? _b : "YouTube embed", frameBorder: "0", allow: "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share", referrerPolicy: "strict-origin-when-cross-origin", allowFullScreen: true }));
                            }
                            return _jsx("img", { ...props, style: { maxWidth: "100%" } });
                        },
                    }, children: finalText }) }), !expanded && enableExpanded && (_jsxs(Text, { onClick: () => setExpanded(true), fw: WEIGHT_BOLD, size: "sm", sx: {
                    alignItems: "center",
                    display: "flex",
                    cursor: "pointer",
                    width: "fit-content",
                }, children: ["Read more\u00A0", _jsx(IconArrowRightBar, { width: 16 })] }))] }));
};
const LinkWithPopupPreview = (props) => {
    var _a;
    const [opened, { close, open }] = useDisclosure(false);
    const [isTextHover, setIsTextHover] = useState(false);
    const [isTooltipHover, setIsTooltipHover] = useState(false);
    useEffect(() => {
        if (!isTooltipHover && !isTextHover) {
            close();
        }
        else if (!opened) {
            open();
        }
    }, [isTooltipHover, isTextHover, opened]);
    const textTimeoutRef = useRef();
    const tooltipTimeoutRef = useRef();
    const href = ((_a = props.node.properties) === null || _a === void 0 ? void 0 : _a.href) || "";
    const collectionId = href.split("collection:")[1];
    const userId = href.split("user:")[1];
    const [position, setPosition] = useState("top");
    const transition = {
        in: { opacity: 1, transform: "translateY(0)" },
        out: {
            opacity: 0,
            transform: `translateY(${position === "top" ? "10" : "-10"}%)`,
        },
        common: { transformOrigin: position === "top" ? "top" : "bottom" },
        transitionProperty: "transform, opacity",
    };
    const [height, setHeight] = useState(0);
    const collectionPreview = !!collectionId && (_jsx(CollectionPreview, { collectionId: collectionId, setHeight: setHeight, height: height }));
    const userPreview = !!userId && (_jsx(UserPreview, { userId: userId, setHeight: setHeight, height: height }));
    if (collectionId || userId) {
        const link = userId
            ? USER_ROUTES.user.profile.replace(":slug", userId)
            : getMintPageUrl({ id: collectionId, onchainId: null });
        return (_jsxs(_Fragment, { children: [_jsxs(Box, { display: "none", children: [collectionPreview, userPreview] }), _jsxs(Popover, { width: 250, position: "bottom", withArrow: true, shadow: "md", opened: opened, transition: transition, transitionDuration: 150, onPositionChange: setPosition, onChange: (e) => console.log("onChange", e, Date.now()), onOpen: () => console.log("onOpen", Date.now()), onClose: () => console.log("onClose", Date.now()), children: [_jsx(Popover.Target, { children: _jsx(UnstyledButton, { onMouseEnter: () => {
                                    if (textTimeoutRef.current) {
                                        clearTimeout(textTimeoutRef.current);
                                    }
                                    setIsTextHover(true);
                                }, onMouseLeave: () => (textTimeoutRef.current = setTimeout(() => setIsTextHover(false), 300)), component: "a", href: link, sx: {
                                    display: "inline-flex",
                                    "&:hover": {
                                        textDecoration: "underline",
                                        textUnderlineOffset: 3,
                                    },
                                }, children: _jsx(Text, { component: "span", size: props.size || 14, weight: WEIGHT_BOLD, sx: { wordBreak: "break-word" }, children: props.children }) }) }), _jsxs(Popover.Dropdown, { onMouseEnter: () => {
                                if (tooltipTimeoutRef.current) {
                                    clearTimeout(tooltipTimeoutRef.current);
                                }
                                if (isTextHover) {
                                    setIsTooltipHover(true);
                                }
                            }, onMouseLeave: () => (tooltipTimeoutRef.current = setTimeout(() => setIsTooltipHover(false), 300)), children: [collectionPreview, userPreview] })] })] }));
    }
    return null;
};
export const loadImage = (setImageDimensions, imageUrl) => {
    const img = new Image();
    img.src = imageUrl;
    img.onload = () => {
        setImageDimensions({
            height: img.height,
            width: img.width,
        });
    };
    img.onerror = (err) => {
        console.log("img error");
        console.error(err);
    };
};
const CollectionPreview = ({ collectionId, setHeight, height, }) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
    const { data: collectionData } = useCollectionPopupPreviewQuery({
        variables: {
            collectionId: collectionId,
        },
        skip: !collectionId,
    });
    const theme = useMantineTheme();
    const collectionDetails = collectionData === null || collectionData === void 0 ? void 0 : collectionData.getPublicCollectionDetails;
    const mpId = (_a = collectionDetails === null || collectionDetails === void 0 ? void 0 : collectionDetails.marketplaceId) !== null && _a !== void 0 ? _a : "";
    const reservoirMpId = (_b = mpId === null || mpId === void 0 ? void 0 : mpId.substring(mpId.indexOf(":") + 1)) !== null && _b !== void 0 ? _b : "";
    const { data } = useCollections({ id: reservoirMpId }, { isPaused: () => !(collectionDetails === null || collectionDetails === void 0 ? void 0 : collectionDetails.marketplaceId) }, collectionDetails === null || collectionDetails === void 0 ? void 0 : collectionDetails.chainId);
    const mpData = data === null || data === void 0 ? void 0 : data[0];
    const imageUrl = collectionDetails === null || collectionDetails === void 0 ? void 0 : collectionDetails.collectionImage;
    const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
    const [isLoaded, setIsLoaded] = useState(false);
    useEffect(() => {
        const newHeight = !imageDimensions.width
            ? 250
            : (250 / imageDimensions.width) * imageDimensions.height;
        if (newHeight) {
            setHeight(newHeight);
        }
    }, [imageDimensions]);
    useEffect(() => {
        if (imageUrl) {
            loadImage(setImageDimensions, imageUrl);
        }
    }, [imageUrl]);
    const network = networkLookup((collectionDetails === null || collectionDetails === void 0 ? void 0 : collectionDetails.chainId) || 1);
    return (_jsxs(Stack, { spacing: 0, children: [_jsx(Box, { h: height, sx: {
                    borderBottom: `1px solid ${theme.colors.divider[0]}`,
                    transition: "height 0.05s ease-out",
                }, mx: -16, mt: -16, children: _jsx(ResizedImage, { src: imageUrl, onLoad: () => setIsLoaded(true), sx: {
                        opacity: isLoaded ? 1 : 0,
                        transition: "opacity 0.15s ease-out",
                    } }) }), _jsxs(Stack, { spacing: 8, mt: 16, children: [_jsx(User, { avatarUrl: (_c = collectionDetails === null || collectionDetails === void 0 ? void 0 : collectionDetails.creatorAccountSettings) === null || _c === void 0 ? void 0 : _c.displayAvatar, displayName: (_d = collectionDetails === null || collectionDetails === void 0 ? void 0 : collectionDetails.creatorAccountSettings) === null || _d === void 0 ? void 0 : _d.displayName, walletAddresses: (_e = collectionDetails === null || collectionDetails === void 0 ? void 0 : collectionDetails.creatorAccountSettings) === null || _e === void 0 ? void 0 : _e.walletAddresses, verified: !!((_f = collectionDetails === null || collectionDetails === void 0 ? void 0 : collectionDetails.creatorAccountSettings) === null || _f === void 0 ? void 0 : _f.verified), imported: !!((_g = collectionDetails === null || collectionDetails === void 0 ? void 0 : collectionDetails.creatorAccountSettings) === null || _g === void 0 ? void 0 : _g.imported), enableLink: true }), _jsx(Text, { size: "lg", fw: WEIGHT_BOLD, children: collectionDetails === null || collectionDetails === void 0 ? void 0 : collectionDetails.name }), ((_k = (_j = (_h = mpData.floorAsk) === null || _h === void 0 ? void 0 : _h.price) === null || _j === void 0 ? void 0 : _j.amount) === null || _k === void 0 ? void 0 : _k.raw) && (_jsxs(Flex, { gap: 4, children: [_jsx(Text, { size: "sm", fw: WEIGHT_BOLD, children: "Floor:" }), _jsx(FormatCrypto, { size: "sm", amount: mpData.floorAsk.price.amount.raw, shorten: true }), _jsx(Text, { size: "sm", children: getNativeCurrencyName(network.currencyName) })] }))] }), _jsx(DescriptionMarkdown, { size: "sm", description: (_l = collectionDetails === null || collectionDetails === void 0 ? void 0 : collectionDetails.description) !== null && _l !== void 0 ? _l : "", noPopover: true, lineClamp: 8 })] }));
};
const UserPreview = ({ userId, setHeight, height, }) => {
    var _a;
    const { data: userData } = useUserPopupPreviewQuery({
        variables: {
            slug: userId,
        },
        skip: !userId,
    });
    const theme = useMantineTheme();
    const user = userData === null || userData === void 0 ? void 0 : userData.getPublicAccountSettings;
    const imageUrl = (user === null || user === void 0 ? void 0 : user.displayAvatar) || (user === null || user === void 0 ? void 0 : user.avatarUrl) || ProfilePlaceholderImage;
    const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
    // containerWidth = 218px
    const name = getProfileDisplayName(user === null || user === void 0 ? void 0 : user.displayName, user === null || user === void 0 ? void 0 : user.walletAddresses, userId);
    const [isLoaded, setIsLoaded] = useState(false);
    useEffect(() => {
        const newHeight = !imageDimensions.width
            ? 250
            : (250 / imageDimensions.width) * imageDimensions.height;
        if (newHeight) {
            setHeight(newHeight);
        }
    }, [imageDimensions]);
    useEffect(() => {
        if (imageUrl) {
            loadImage(setImageDimensions, imageUrl);
        }
    }, [imageUrl]);
    return (_jsxs(Stack, { spacing: 0, onClick: (e) => e.stopPropagation(), children: [_jsx(Box, { pos: "relative", h: height, sx: {
                    borderBottom: `1px solid ${theme.colors.divider[0]}`,
                    transition: "height 0.05s ease-out",
                }, mx: -16, mt: -16, children: _jsx(ResizedImage, { src: imageUrl, onLoad: () => setIsLoaded(true), sx: {
                        opacity: isLoaded ? 1 : 0,
                        transition: "opacity 0.15s ease-out",
                    } }) }), _jsxs(Group, { mt: "md", spacing: "xs", align: "center", children: [_jsx(Text, { size: "lg", fw: WEIGHT_BOLD, weight: WEIGHT_BOLD, children: name }), (user === null || user === void 0 ? void 0 : user.verified) && _jsx(CheckVerified, { height: 20, width: 20 }), !(user === null || user === void 0 ? void 0 : user.verified) && (user === null || user === void 0 ? void 0 : user.imported) && (_jsx(CheckImported, { height: 20, width: 20 }))] }), _jsx(DescriptionMarkdown, { size: "sm", description: (_a = user === null || user === void 0 ? void 0 : user.bio) !== null && _a !== void 0 ? _a : "", mb: 8, noPopover: true, lineClamp: 8 }), user && _jsx(Socials, { profile: user })] }));
};
export default memo(DescriptionMarkdown);
